import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { ChevronDown, ArrowRight, Sparkles, Factory, Award, Users, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import heroImage from '@/assets/hero-bg.jpg';

// Add custom styles for glossy white text effect
const glossyWhiteStyles = `
  .text-glow {
    text-shadow: 
      0 0 10px rgba(255, 255, 255, 0.8),
      0 0 20px rgba(255, 255, 255, 0.6),
      0 0 30px rgba(255, 255, 255, 0.4),
      0 0 40px rgba(255, 255, 255, 0.2);
    filter: brightness(1.2) contrast(1.1);
  }
`;
const Hero = () => {
  const { t } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return <>
    <style dangerouslySetInnerHTML={{ __html: glossyWhiteStyles }} />
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Enhanced Background with layered effects */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url('/iac.jpg')`,
        contentVisibility: 'auto',
        containIntrinsicSize: '100%'
      }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-slate-800/40 to-slate-700/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,theme(colors.blue.500/0.15),transparent_80%)]"></div>
      </div>

      {/* Animated floating elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          variants={floatingVariants}
          animate="animate"
          className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-cyan-500/10 rounded-full blur-xl"
        ></motion.div>
        <motion.div 
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '2s' }}
          className="absolute top-3/4 right-1/4 w-24 h-24 bg-gradient-to-br from-purple-500/20 to-pink-500/10 rounded-full blur-lg"
        ></motion.div>
        <motion.div 
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '4s' }}
          className="absolute top-1/2 left-3/4 w-40 h-40 bg-gradient-to-br from-green-500/10 to-teal-500/5 rounded-full blur-2xl"
        ></motion.div>
      </div>

      {/* Content */}
      <motion.div 
        className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-5xl mx-auto">
          {/* Modern Badge - Translated to Slovak and moved down */}
          

          {/* Main heading with typing effect and softer colors */}
          <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl lg:text-8xl font-display font-bold mb-8">
            <span className="block leading-tight">
              {/* IAC GROUP with typing effect - No glow */}
              <span className="text-white">
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 0.5 }}
                  className="inline-block"
                >
                  I
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 0.6 }}
                  className="inline-block"
                >
                  A
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 0.7 }}
                  className="inline-block"
                >
                  C
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 0.8 }}
                  className="inline-block"
                >
                  &nbsp;
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 0.9 }}
                  className="inline-block"
                >
                  G
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.0 }}
                  className="inline-block"
                >
                  R
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.1 }}
                  className="inline-block"
                >
                  O
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.2 }}
                  className="inline-block"
                >
                  U
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.3 }}
                  className="inline-block"
                >
                  P
                </motion.span>
                <motion.span
                  initial={{ opacity: 1 }}
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, times: [0, 1], delay: 1.4 }}
                  className="inline-block w-1 h-16 bg-white ml-1 animate-pulse drop-shadow-[0_0_5px_rgba(255,255,255,0.8)]"
                >
                </motion.span>
              </span>
            </span>
            <span className="block text-3xl md:text-4xl lg:text-5xl text-white font-semibold mt-2">
              <span className="text-white">
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.5 }}
                  className="inline-block"
                >
                  (
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.6 }}
                  className="inline-block"
                >
                  S
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.7 }}
                  className="inline-block"
                >
                  L
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.8 }}
                  className="inline-block"
                >
                  O
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 1.9 }}
                  className="inline-block"
                >
                  V
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 2.0 }}
                  className="inline-block"
                >
                  A
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 2.1 }}
                  className="inline-block"
                >
                  K
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 2.2 }}
                  className="inline-block"
                >
                  I
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 2.3 }}
                  className="inline-block"
                >
                  A
                </motion.span>
                <motion.span
                  initial=""
                  animate={{ display: ["none", "inline"] }}
                  transition={{ duration: 0, times: [0, 0.05], delay: 2.4 }}
                  className="inline-block"
                >
                  )
                </motion.span>
              </span>
            </span>
          </motion.h1>
          
          {/* Enhanced tagline */}
          <motion.div variants={itemVariants} className="max-w-4xl mx-auto mb-8">
            <p className="text-2xl md:text-3xl lg:text-4xl font-display font-medium text-white leading-tight">
              {t('hero.tagline')}
            </p>
          </motion.div>
          
          {/* Description with better styling */}
          <motion.div variants={itemVariants} className="max-w-3xl mx-auto mb-12">
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              {t('hero.description')}
            </p>
          </motion.div>

          {/* Enhanced buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <motion.button 
              className="relative group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-medium rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {t('hero.learnMore')}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </motion.button>
            
            <motion.button 
              className="px-8 py-4 bg-transparent border-2 border-white/20 text-white text-lg font-medium rounded-xl backdrop-blur-sm hover:bg-white/10 hover:border-white/30 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('hero.contactUs')}
            </motion.button>
          </motion.div>

          {/* Modern Stats */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
              <Factory className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">24+</div>
              <div className="text-sm text-gray-400">Rokov skúseností</div>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
              <Award className="w-8 h-8 text-purple-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">ISO</div>
              <div className="text-sm text-gray-400">Certifikovaná</div>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
              <Users className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">500+</div>
              <div className="text-sm text-gray-400">Zamestnancov</div>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
              <Globe className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">Globalný</div>
              <div className="text-sm text-gray-400">Dosah</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Modern scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
      >
        <motion.div 
          className="flex flex-col items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer group"
          whileHover={{ scale: 1.1 }}
        >
          <span className="text-xs font-medium">Scroll Down</span>
          <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center group-hover:border-white transition-colors">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown className="w-4 h-4 mt-2" />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  </>;
};
export default Hero;